import React, { useEffect, useRef } from 'react';
import './LivePreview.css';
import './LivePreviewFrame.css';
import { ensureInteractivePreviewDoc } from '../utils/ensureInteractivePreviewDoc.js';

/**
 * Interactive Preview panel â€” self-contained HTML/CSS/JS (or React+Tailwind via CDN)
 * rendered in a sandboxed iframe. Buttons, forms, and state run live in the browser.
 */
export default function LivePreview({
  html = '',
  css = '',
  js = '',
  fullDoc = '', // optional: full HTML document (if provided, overrides html/css/js composition)
  isVisible = true,
  onRuntimeEvent,
  errorOverlay,
  hostMessage, // optional: { type: string, ... } - sent to iframe via postMessage
  viewportWidth, // number (px) - optional
  frameDevice = false, // boolean - adds a device-like frame
  enableAnalytics = false, // boolean - click/scroll events
  enableAutomation = false, // boolean - capture form leads
  enablePreviewEdit = false, // boolean - click element in preview to edit its text
  hideChrome = false, // when true (embedded Preview panel), no extra "Live Preview" header
}) {
  const iframeRef = useRef(null);
  const hostMessageRef = useRef(null);

  useEffect(() => {
    hostMessageRef.current = hostMessage;
  }, [hostMessage]);

  useEffect(() => {
    if (!onRuntimeEvent) return;

    const handler = (event) => {
      const iframeWindow = iframeRef.current?.contentWindow;
      if (!iframeWindow) return;
      if (event.source !== iframeWindow) return;
      if (!event.data || event.data.__zolaidPreview !== true) return;
      onRuntimeEvent(event.data.payload);
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onRuntimeEvent]);

  useEffect(() => {
    if (!iframeRef.current) return;

    const iframe = iframeRef.current;
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

    const instrumentationJS = `
      (function () {
        const safeToString = (v) => {
          try {
            if (typeof v === 'string') return v;
            if (v instanceof Error) return v.stack || v.message || String(v);
            return JSON.stringify(v, (_k, value) => {
              if (typeof value === 'bigint') return value.toString();
              return value;
            });
          } catch (_) {
            try { return String(v); } catch (_) { return '[unserializable]'; }
          }
        };

        const send = (payload) => {
          try {
            parent.postMessage({ __zolaidPreview: true, payload }, '*');
          } catch (_) {}
        };

        // Console bridge
        ['log', 'info', 'warn', 'error', 'debug'].forEach((level) => {
          const orig = console[level];
          console[level] = function (...args) {
            send({
              type: 'console',
              level,
              args: args.map(safeToString),
              ts: Date.now(),
            });
            try { return orig && orig.apply(console, args); } catch (_) {}
          };
        });

        // Errors
        window.addEventListener('error', (e) => {
          send({
            type: 'error',
            message: e.message || 'Unknown error',
            filename: e.filename,
            lineno: e.lineno,
            colno: e.colno,
            stack: e.error && (e.error.stack || String(e.error)),
            ts: Date.now(),
          });
        });

        window.addEventListener('unhandledrejection', (e) => {
          send({
            type: 'unhandledrejection',
            reason: safeToString(e.reason),
            ts: Date.now(),
          });
        });

        // UI interceptions
        const origAlert = window.alert;
        window.alert = function (msg) {
          send({ type: 'alert', message: safeToString(msg), ts: Date.now() });
          // Don't block the sandbox with modal alerts
        };

        const origOpen = window.open;
        window.open = function (...args) {
          send({ type: 'window.open', args: args.map(safeToString), ts: Date.now() });
          return null;
        };

        send({ type: 'ready', ts: Date.now() });

        window.addEventListener('message', function (e) {
          try {
            if (!e.data || e.data.__zolaidHost !== true) return;
            var pl = e.data.payload;
            if (!pl || pl.type !== 'scroll-to-hash') return;
            var raw = String(pl.hash || '').trim();
            var id = raw.replace(/^#/, '');
            if (!id) {
              try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) { window.scrollTo(0, 0); }
              return;
            }
            var el = document.getElementById(id);
            if (el && el.scrollIntoView) {
              el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
              try { location.hash = '#' + id; } catch (_) {}
            }
          } catch (_) {}
        });

        ${enablePreviewEdit ? `
        // Click-to-edit: send element text to host so user can edit and apply
        const EDITABLE_TAGS = ['h1','h2','h3','h4','h5','h6','p','span','a','li','td','th','label','button','figcaption','caption','dd','dt','blockquote'];
        try {
          var style = document.createElement('style');
          style.textContent = EDITABLE_TAGS.join(',') + ' { cursor: pointer; }';
          (document.head || document.documentElement).appendChild(style);
        } catch (_) {}
        document.addEventListener('click', (e) => {
          try {
            const el = e && e.target ? e.target : null;
            if (!el || !el.tagName) return;
            const tag = String(el.tagName).toLowerCase();
            if (EDITABLE_TAGS.indexOf(tag) === -1) return;
            const text = (el.textContent || '').trim();
            if (text.length === 0) return;
            e.preventDefault();
            e.stopPropagation();
            send({ type: 'preview-edit-request', text: text, tagName: tag, ts: Date.now() });
          } catch (_) {}
        }, true);
        ` : ''}

        ${enableAnalytics ? `
        // Basic analytics (prototype): click + scroll depth
        const sid = (function () {
          try { return 'sess_' + Date.now() + '_' + Math.random().toString(16).slice(2); } catch (_) { return 'sess_' + Date.now(); }
        })();
        send({ type: 'analytics_session', sessionId: sid, ts: Date.now() });

        document.addEventListener('click', (e) => {
          try {
            const t = e && e.target ? e.target : null;
            const tag = t && t.tagName ? String(t.tagName).toLowerCase() : '';
            send({ type: 'analytics', event: { type: 'click', tag, ts: Date.now() } });
          } catch (_) {}
        }, true);

        let lastPct = 0;
        window.addEventListener('scroll', () => {
          try {
            const doc = document.documentElement;
            const scrollTop = (window.scrollY || doc.scrollTop || 0);
            const maxScroll = Math.max(1, (doc.scrollHeight || 1) - (window.innerHeight || 1));
            const pct = Math.max(0, Math.min(100, Math.round((scrollTop / maxScroll) * 100)));
            if (pct >= lastPct + 5 || pct === 100) {
              lastPct = pct;
              send({ type: 'analytics', event: { type: 'scroll', scrollPct: pct, ts: Date.now() } });
            }
          } catch (_) {}
        }, { passive: true });
        ` : ``}

        ${enableAutomation ? `
        // Automation hook (prototype): capture form submissions as leads
        document.addEventListener('submit', (e) => {
          try {
            const form = e && e.target ? e.target : null;
            if (!form || !form.tagName || String(form.tagName).toLowerCase() !== 'form') return;
            e.preventDefault(); // keep preview stable
            const fields = {};
            try {
              const fd = new FormData(form);
              for (const [k, v] of fd.entries()) {
                const key = String(k || '').slice(0, 60);
                if (!key) continue;
                const val = (typeof v === 'string') ? v : (v && v.name ? ('[file:' + v.name + ']') : '[file]');
                fields[key] = String(val || '').slice(0, 500);
              }
            } catch (_) {}

            send({
              type: 'lead',
              lead: {
                form: {
                  action: form.getAttribute('action') || '',
                  method: form.getAttribute('method') || 'GET',
                },
                fields,
                note: 'Captured in preview sandbox',
                ts: Date.now(),
              },
            });

            // Friendly UX feedback inside sandbox (non-blocking)
            try {
              const msg = document.createElement('div');
              msg.textContent = 'Lead captured (demo).';
              msg.style.position = 'fixed';
              msg.style.right = '12px';
              msg.style.bottom = '12px';
              msg.style.padding = '10px 12px';
              msg.style.borderRadius = '12px';
              msg.style.background = 'rgba(17,24,39,0.92)';
              msg.style.color = '#fff';
              msg.style.font = '600 12px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
              msg.style.zIndex = '99999';
              document.body.appendChild(msg);
              setTimeout(() => msg.remove(), 1800);
            } catch (_) {}
          } catch (_) {}
        }, true);
        ` : ``}
      })();
    `;

    const injectInstrumentation = (doc) => {
      try {
        const d = String(doc || '');
        const marker = `<script>\n${instrumentationJS}\n</script>\n`;
        if (d.includes('__zolaidPreview')) return d; // already instrumented
        if (d.includes('</body>')) return d.replace('</body>', `${marker}</body>`);
        if (d.includes('</html>')) return d.replace('</html>', `${marker}</html>`);
        return d + marker;
      } catch (_) {
        return String(doc || '');
      }
    };

    // Self-contained Preview panel document (Tailwind/React CDNs when needed)
    const fullHTML = (() => {
      const prepared = ensureInteractivePreviewDoc({
        fullDoc: String(fullDoc || '').trim(),
        html,
        css,
        js,
        title: 'Preview panel',
      });
      return injectInstrumentation(prepared);
    })();

    iframeDoc.open();
    iframeDoc.write(fullHTML);
    iframeDoc.close();

    // After reload, re-send the last hostMessage (best-effort) so selection/navigation stays in sync.
    try {
      const win = iframeRef.current?.contentWindow;
      const msg = hostMessageRef.current;
      if (win && msg && typeof msg === 'object') {
        window.setTimeout(() => {
          try {
            win.postMessage({ __zolaidHost: true, payload: msg }, '*');
          } catch (_) {}
        }, 50);
      }
    } catch (_) {}
  }, [html, css, js, fullDoc, enablePreviewEdit, isVisible]);

  // Host -> iframe message channel (for things like navigation/selection)
  useEffect(() => {
    try {
      const win = iframeRef.current?.contentWindow;
      if (!win) return;
      if (!hostMessage || typeof hostMessage !== 'object') return;
      const send = () => {
        try {
          win.postMessage({ __zolaidHost: true, payload: hostMessage }, '*');
        } catch (_) {}
      };
      // Let iframe document/layout settle so scroll-to-hash finds #section (e.g. #about).
      if (hostMessage.type === 'scroll-to-hash') {
        window.setTimeout(send, 120);
      } else {
        send();
      }
    } catch (_) {}
  }, [hostMessage]);

  if (!isVisible) return null;

  const hasContent = (fullDoc && String(fullDoc).trim()) || (html && String(html).trim());

  return (
    <div className={`live-preview-container ${hideChrome ? 'live-preview-embedded' : ''}`}>
      {!hideChrome && (
        <div className="preview-header">
          <span className="preview-icon">ðŸŒ</span>
          <span className="preview-title">Preview panel</span>
        </div>
      )}
      <div className="preview-content">
        <div className="preview-iframe-wrapper">
          <div
            className={`preview-iframe-frame ${frameDevice ? 'is-device' : ''}`}
            style={viewportWidth ? { maxWidth: `${viewportWidth}px` } : undefined}
          >
        {!hasContent && (
          <div className="preview-empty-state">
            <span className="preview-empty-icon" aria-hidden>ðŸŒ</span>
            <p className="preview-empty-title">No preview yet</p>
            <p className="preview-empty-hint">Add HTML in Code or Builder to see the live output here.</p>
          </div>
        )}
        {errorOverlay && (
          <div className="preview-error-overlay">
            <div className="preview-error-title">{errorOverlay.title || 'Runtime error'}</div>
            <div className="preview-error-message">{errorOverlay.message || ''}</div>
          </div>
        )}
        <iframe
          ref={iframeRef}
          className="preview-iframe"
          title="Live Preview"
              allow="xr-spatial-tracking; fullscreen"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
        />
          </div>
        </div>
      </div>
    </div>
  );
}

